import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import ActionPalette from './action/ActionPalette';
import { getStateCatalogStatus } from './action/stateLayerCatalog';
import type { ActionArgSuggestion, ActionCommandDef } from './action/types';
import useActionMode from './action/useActionMode';

type ArgSourceFilter = 'all' | 'state' | 'global';
const MAX_VISIBLE_ARG_SUGGESTIONS = 25;

function selectPSAPStateCode(state: unknown): string | undefined {
  const rawState = (state as { psaps?: { currentPSAP?: { state?: unknown } } })
    ?.psaps?.currentPSAP?.state;
  if (typeof rawState !== 'string') return undefined;
  const normalized = rawState.trim().toUpperCase();
  return /^[A-Z]{2}$/.test(normalized) ? normalized : undefined;
}

function matchesSourceFilter(
  suggestion: ActionArgSuggestion,
  filter: ArgSourceFilter,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'state') {
    return suggestion.source === 'state' || suggestion.source === 'both';
  }
  return suggestion.source === 'global' || suggestion.source === 'both';
}

/**
 * Imperative surface the parent shell talks to. Mirrors the slash
 * experience handle so the host's routing can flip between the two
 * with the same call shape.
 */
export interface ActionExperienceHandle {
  /** Forwarded keystrokes — only consumed while the palette is shown. */
  onKeyDown: (e: React.KeyboardEvent) => void;
  /** Wipe action-only state (called from the shell's clear button). */
  onClear: () => void;
  /** Re-open the palette on input focus. */
  onFocus: () => void;
}

interface ActionCommandExperienceProps {
  searchTerm: string;
  setSearchTerm: (next: string) => void;
  /**
   * True while the input does NOT start with `//`. The component
   * still mounts (so its hooks/state survive a round-trip through
   * the other modes) but skips the dropdown render.
   */
  isHidden: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  isFocusedRef: React.MutableRefObject<boolean>;
  /**
   * Bubbles the inline-completion suffix that should render as ghost
   * text right after the typed input. Mirrors what `Tab` will fill
   * (always the *first* suggestion). Empty string when there's
   * nothing to complete.
   */
  onGhostSuffixChange?: (next: string) => void;
}

/**
 * Owns the entire `//`-prefix workflow end-to-end: parsing, action
 * autocomplete, palette UI, and keyboard handling. Suggestion-only —
 * picking an action or arg fills the canonical input text but does
 * NOT dispatch any side effect (a future tier can add execution
 * without changing this shell).
 *
 * Strictly siloed from the slash command system in `command/`: the
 * two surfaces share nothing beyond the input element and focus
 * refs, so changes here can't regress single-slash behavior.
 */
const ActionCommandExperience = forwardRef<
  ActionExperienceHandle,
  ActionCommandExperienceProps
>(function ActionCommandExperience(
  {
    searchTerm,
    setSearchTerm,
    isHidden,
    inputRef,
    isFocusedRef,
    onGhostSuffixChange,
  },
  ref,
) {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Cursor is intentionally separate from the slash workflow's
  // cursor: each owns its own row index against its own list.
  const [cursor, setCursor] = useState(-1);

  // Lets the user dismiss the palette without clearing the input.
  // Re-opens automatically on any keystroke or refocus so the palette
  // feels persistent.
  const [isPaletteOpen, setIsPaletteOpen] = useState(true);

  // Track arg rows whose execute path is currently in flight so we
  // can suppress duplicate picks while the load resolves.
  const [loadingArgIds, setLoadingArgIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [argSourceFilter, setArgSourceFilter] =
    useState<ArgSourceFilter>('all');
  const psapStateCode = useSelector(selectPSAPStateCode);

  const action = useActionMode(searchTerm);
  const argSourceCounts = useMemo(
    () =>
      action.argSuggestions.reduce(
        (acc, suggestion) => {
          if (suggestion.source === 'state' || suggestion.source === 'both') {
            acc.state += 1;
          }
          if (
            suggestion.source === 'global' ||
            suggestion.source === 'both' ||
            suggestion.source === undefined
          ) {
            acc.global += 1;
          }
          return acc;
        },
        { state: 0, global: 0 },
      ),
    [action.argSuggestions],
  );

  const filteredArgSuggestions = useMemo(
    () =>
      action.argSuggestions.filter(suggestion =>
        matchesSourceFilter(suggestion, argSourceFilter),
      ),
    [action.argSuggestions, argSourceFilter],
  );

  const visibleArgSuggestions = useMemo(
    () => filteredArgSuggestions.slice(0, MAX_VISIBLE_ARG_SUGGESTIONS),
    [filteredArgSuggestions],
  );

  const isStateCatalogLoading =
    action.isArgMode &&
    action.committedAction?.id === 'add' &&
    getStateCatalogStatus(psapStateCode) === 'pending';

  // Reset the keyboard cursor whenever the palette swaps between
  // its two views (catalog ↔ arg suggestions). Without this the
  // highlight would carry over to a different list and point at
  // the wrong row.
  useEffect(() => {
    setCursor(-1);
    setArgSourceFilter('all');
  }, [action.isArgMode]);

  useEffect(() => {
    setCursor(-1);
  }, [argSourceFilter]);

  // Re-open on any keystroke that mutates the input (mirrors the
  // slash experience's same effect).
  useEffect(() => {
    if (!isHidden) setIsPaletteOpen(true);
  }, [isHidden, searchTerm]);

  // Keep the keyboard-selected row visible inside the palette.
  useEffect(() => {
    if (cursor < 0 || !dropdownRef.current) return;
    const el = dropdownRef.current.querySelector(
      `[data-result-index="${cursor}"]`,
    ) as HTMLElement | null;
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [cursor]);

  // --- Pick handlers ---
  // Picking an action in catalog mode autofills `//<id> ` so the
  // user can immediately type (or pick) the arg. Suggestion-only
  // means there's no "execute now" path to branch on here.
  const handlePickAction = useCallback(
    (def: ActionCommandDef) => {
      const next = `//${def.id} `;
      setSearchTerm(next);
      setCursor(-1);
      inputRef.current?.focus();
    },
    [setSearchTerm, inputRef],
  );

  // Pulled out of `action` here (not inside the callback) so the
  // useCallback dep array references plain locals — the
  // react-hooks/exhaustive-deps rule trips on async callbacks that
  // dep on member-access expressions (`action.executeArg`) and asks
  // for the parent (`action`), but depending on the whole object
  // means the callback re-creates on every render where any other
  // `action.*` field changes (parsed, suggestions, etc.). Local
  // destructuring sidesteps both problems.
  const { committedAction, executeArg } = action;

  // Picking an arg has two flavors based on the def:
  //   - `execute` defined → run execute, clear/blur on success so the
  //     palette closes cleanly. Failures leave the input alone so
  //     the user can retry / pick a different row.
  //   - `execute` undefined → fall back to suggestion-only autofill
  //     (canonical text into the input, no side effect).
  //
  // The loading-set add/remove is wrapped in try/finally so a
  // throwing executor doesn't strand a row in the spinner state
  // forever — `executeArg` already catches and reports synchronous
  // throws, but defense-in-depth here is essentially free.
  const handlePickArg = useCallback(
    async (suggestion: ActionArgSuggestion) => {
      if (!committedAction) return;
      if (loadingArgIds.has(suggestion.id)) return;

      const executePromise = executeArg(suggestion);
      if (executePromise) {
        setLoadingArgIds(prev => {
          if (prev.has(suggestion.id)) return prev;
          const next = new Set(prev);
          next.add(suggestion.id);
          return next;
        });
        try {
          const result = await executePromise;
          if (result.ok) {
            setSearchTerm('');
            setCursor(-1);
            isFocusedRef.current = false;
            inputRef.current?.blur();
            return;
          }
          // eslint-disable-next-line no-console
          console.error(
            `[GlobalMapSearch/action] //${committedAction.id} failed:`,
            result.message,
          );
        } finally {
          setLoadingArgIds(prev => {
            if (!prev.has(suggestion.id)) return prev;
            const next = new Set(prev);
            next.delete(suggestion.id);
            return next;
          });
        }
        return;
      }

      setSearchTerm(`//${committedAction.id} ${suggestion.value}`);
      setCursor(-1);
      inputRef.current?.focus();
    },
    [
      committedAction,
      executeArg,
      loadingArgIds,
      setSearchTerm,
      inputRef,
      isFocusedRef,
    ],
  );

  // --- Tab completion ---
  // Same model as the slash experience: Tab fills whatever the
  // ghost text previewed (currently highlighted row, or first row
  // when nothing is highlighted).
  const handleTabComplete = useCallback(() => {
    if (action.isArgMode) {
      const target =
        visibleArgSuggestions[cursor >= 0 ? cursor : 0] ??
        visibleArgSuggestions[0];
      const def = action.committedAction;
      if (!def || !target) return;
      setSearchTerm(`//${def.id} ${target.value}`);
      setCursor(-1);
      inputRef.current?.focus();
      return;
    }
    const target =
      action.suggestions[cursor >= 0 ? cursor : 0] ?? action.suggestions[0];
    if (!target) return;
    setSearchTerm(`//${target.id} `);
    setCursor(-1);
    inputRef.current?.focus();
  }, [action, cursor, setSearchTerm, inputRef, visibleArgSuggestions]);

  // --- Ghost text ---
  // Computes the suffix that, when concatenated to the typed input,
  // produces what Tab would fill. Visually rendered by the shell as
  // muted text right after the cursor.
  const ghostSuffix = useMemo(() => {
    if (isHidden) return '';
    const idx = cursor >= 0 ? cursor : 0;
    if (action.isArgMode) {
      const target = visibleArgSuggestions[idx] ?? visibleArgSuggestions[0];
      if (!target) return '';
      const typed = action.parsed.rest;
      if (!target.value.toLowerCase().startsWith(typed.toLowerCase())) {
        return '';
      }
      return target.value.slice(typed.length);
    }
    const target = action.suggestions[idx] ?? action.suggestions[0];
    if (!target) return '';
    const typed = action.parsed.name;
    if (!target.id.toLowerCase().startsWith(typed.toLowerCase())) return '';
    // Trailing space mirrors what Tab fills (`//<id> `) so the user
    // can immediately type the arg without an extra keystroke.
    return `${target.id.slice(typed.length)} `;
  }, [
    isHidden,
    cursor,
    action.isArgMode,
    visibleArgSuggestions,
    action.suggestions,
    action.parsed.rest,
    action.parsed.name,
  ]);

  // useLayoutEffect (NOT useEffect): the bubbled suffix needs to
  // land in the shell's state *before* the browser paints, otherwise
  // every keystroke renders one frame with the new typed text against
  // the previous suffix — visible as a flicker.
  useLayoutEffect(() => {
    onGhostSuffixChange?.(ghostSuffix);
  }, [ghostSuffix, onGhostSuffixChange]);

  // Wipe the bubbled suffix when action mode hides so a stale ghost
  // doesn't linger in the other workflows.
  useEffect(() => {
    if (isHidden) onGhostSuffixChange?.('');
  }, [isHidden, onGhostSuffixChange]);

  // --- Imperative surface for the shell ---
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isHidden) return;

      // Tab autocomplete-without-execute. Captured early so browsers
      // don't move focus off the input.
      if (
        e.key === 'Tab' &&
        !e.shiftKey &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        e.preventDefault();
        handleTabComplete();
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        // Enter routes to the same `handlePickArg` path as a click,
        // which checks the def for `execute` and either runs it or
        // falls back to autofill. The handler is async (executes
        // dispatch a thunk + wait for ArcGIS load) so we fire-and-
        // forget here — the palette stays interactive while the
        // network round-trip resolves.
        if (action.isArgMode) {
          if (visibleArgSuggestions.length > 0) {
            const target =
              visibleArgSuggestions[cursor >= 0 ? cursor : 0] ??
              visibleArgSuggestions[0];
            if (target) void handlePickArg(target);
          }
          return;
        }
        if (action.suggestions.length > 0) {
          const target = action.suggestions[cursor >= 0 ? cursor : 0];
          if (target) handlePickAction(target);
        }
        return;
      }

      if (e.key === 'Escape') {
        setCursor(-1);
        isFocusedRef.current = false;
        inputRef.current?.blur();
        return;
      }

      const navCount = action.isArgMode
        ? visibleArgSuggestions.length
        : action.suggestions.length;
      if (navCount === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor(prev => Math.min(prev + 1, navCount - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor(prev => {
          const next = prev - 1;
          if (next < 0) {
            inputRef.current?.focus();
            return -1;
          }
          return next;
        });
      }
    },
    [
      isHidden,
      action.isArgMode,
      visibleArgSuggestions,
      action.suggestions,
      cursor,
      handleTabComplete,
      handlePickAction,
      handlePickArg,
      inputRef,
      isFocusedRef,
    ],
  );

  const handleClear = useCallback(() => {
    setCursor(-1);
    setArgSourceFilter('all');
    setIsPaletteOpen(true);
  }, []);

  const handleFocus = useCallback(() => {
    setIsPaletteOpen(true);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: handleKeyDown,
      onClear: handleClear,
      onFocus: handleFocus,
    }),
    [handleKeyDown, handleClear, handleFocus],
  );

  if (isHidden) return null;
  if (!isPaletteOpen) return null;

  return (
    <ActionPalette
      dropdownRef={dropdownRef}
      suggestions={action.suggestions}
      isCatalog={action.parsed.name === ''}
      isArgMode={action.isArgMode}
      argSuggestions={visibleArgSuggestions}
      argActionId={action.committedAction?.id}
      argSourceFilter={argSourceFilter}
      argSourceCounts={argSourceCounts}
      isStateCatalogLoading={isStateCatalogLoading}
      onArgSourceFilterChange={setArgSourceFilter}
      cursor={cursor}
      onCursorChange={setCursor}
      onPickAction={handlePickAction}
      onPickArg={handlePickArg}
    />
  );
});

export default ActionCommandExperience;
